import { Matrix2D } from './matrix2d';
import { parseAsNumericalArray, Point, Size } from "./geom";
export class Rect {
    static parse(rect) {
        let arr = parseAsNumericalArray(rect);
        if (arr && arr.length === 4) {
            return { x: arr[0], y: arr[1], width: arr[2], height: arr[3] };
        }
        throw new Error(`Cannot parse "${rect}" as a valid Rect.`);
    }
    static isRect(obj) {
        return Point.isPoint(obj) && Size.isSize(obj);
    }
    static _stretch(from, to) {
        return { a: to.width / from.width, b: 0, c: 0, d: to.height / from.height, e: to.x - from.x, f: to.y - from.y };
    }
    static _contain(from, to, align, valign) {
        let scale = 1;
        const fromRatio = from.width / from.height, toRatio = to.width / to.height;
        if (fromRatio >= toRatio) {
            // constraining width
            scale = to.width / from.width;
        }
        else {
            // constraining height
            scale = to.height / from.height;
        }
        const w = from.width * scale, h = from.height * scale;
        const translation = this._align({ x: from.x, y: from.y, width: w, height: h }, to, align, valign);
        return Matrix2D.translate({ a: scale, b: 0, c: 0, d: scale, e: 0, f: 0 }, translation);
    }
    static _cover(from, to, align, valign) {
        let scale = 1;
        const fromRatio = from.width / from.height, toRatio = to.width / to.height;
        if (fromRatio < toRatio) {
            // constraining width
            scale = to.width / from.width;
        }
        else {
            // constraining height
            scale = to.height / from.height;
        }
        const w = from.width * scale, h = from.height * scale;
        const translation = this._align({ x: from.x, y: from.y, width: w, height: h }, to, align, valign);
        return Matrix2D.translate({ a: scale, b: 0, c: 0, d: scale, e: 0, f: 0 }, translation);
    }
    static _align(from, to, align, valign) {
        let x = 0, y = 0;
        switch (align) {
            case 'left':
                x = to.x - from.x;
                break;
            case 'right':
                x = (to.x + to.width - from.width) - from.x;
                break;
            default:
                x = (to.x + to.width * .5 - from.width * .5) - from.x;
                break;
        }
        switch (valign) {
            case 'top':
                y = to.y - from.y;
                break;
            case 'bottom':
                y = (to.y + to.height - from.height) - from.y;
                break;
            default:
                y = (to.y + to.height * .5 - from.height * .5) - from.y;
                break;
        }
        return { x, y };
    }
    /**
     * Computes the 2D matrix that transforms one rect into another.
     * @param a Rect from
     * @param b Rect to
     * @param mode Transformation mode
     * @param align Horizontal alignment
     * @param valign Vertical alignment
     */
    static findTransform(a, b, mode = 'stretch', align = 'center', valign = 'middle') {
        switch (mode) {
            case 'contain':
                return this._contain(a, b, align, valign);
            case 'cover':
                return this._cover(a, b, align, valign);
            default:
                return this._stretch(a, b);
        }
    }
    /**
     * Computes the intersection rect among a set of provided rects.
     * @param rects Rects to intersect
     */
    static intersect(...rects) {
        const empty = { x: 0, y: 0, height: 0, width: 0 }, arr = Array.from(rects || []);
        if (arr.length <= 0) {
            return empty;
        }
        var rect = arr[0];
        for (var i = 1; i < arr.length; i++) {
            const r = arr[i];
            const x = Math.max(r.x, rect.x), y = Math.max(r.y, rect.y), xw = Math.min(r.x + r.width, rect.x + rect.width), xh = Math.min(r.y + r.height, rect.y + rect.height), w = xw - x, h = xh - y;
            if (w < 0 || h <= 0) {
                // no intersection
                return empty;
            }
            rect = { x: x, y: y, width: w, height: h };
        }
        return rect;
    }
    static expand(rect, ...args) {
        const arr = Array.from(args || []);
        let x0 = rect.x, y0 = rect.y, x1 = x0, y1 = y0;
        if (Rect.isRect(rect)) {
            x1 += rect.width;
            y1 += rect.height;
        }
        for (let p of arr) {
            x0 = Math.min(p.x, x0);
            y0 = Math.min(p.y, y0);
            x1 = Math.max(p.x, x1);
            y1 = Math.max(p.y, y1);
        }
        return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    }
}
//}
